import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, Loader2, ArrowUpRight, FileText,
  AlertCircle, Check, Clock, X,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/dates";

const DOC_TYPES = ["drivers_license", "passport", "state_id", "business_license", "other"];

export default function VerificationPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/verification/status");
      setStatus(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("stripe_identity") === "return") {
      setFeedback({ type: "info", message: t("verificationX.stripeReturn") });
      const next = new URLSearchParams(params);
      next.delete("stripe_identity");
      setParams(next, { replace: true });
      let attempts = 0;
      const tt = setInterval(async () => {
        attempts += 1;
        await load();
        if (attempts >= 5) clearInterval(tt);
      }, 4000);
      return () => clearInterval(tt);
    }
  }, [params, setParams, load, t]);

  const startStripeIdentity = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/stripe/start");
      window.location.href = data.url;
    } catch (err) {
      setFeedback({ type: "error", message: errorMessage(err, t("verificationX.stripeStartFail")) });
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="provider">
        <div className="flex items-center justify-center py-24 text-smoke">
          <Loader2 className="animate-spin mr-2" /> {t("verificationX.loading")}
        </div>
      </DashboardLayout>
    );
  }

  const isVerified = status?.is_verified;
  const pending = status?.pending_request;

  return (
    <DashboardLayout role="provider">
      <div data-testid="verification-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className={`rounded-md p-2.5 ${isVerified ? "bg-hive text-worker" : "bg-worker text-cream"}`}>
                <ShieldCheck size={26} />
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
                {t("verificationX.title")}
              </h1>
            </div>
            <p className="mt-3 text-smoke max-w-xl" dangerouslySetInnerHTML={{ __html: t("verificationX.subtitle") }} />
          </div>
        </div>

        {feedback && (
          <div className={`mb-6 p-3 rounded-md border flex items-start gap-2 text-sm font-display ${
            feedback.type === "success" ? "bg-green-50 border-green-300 text-green-800" :
            feedback.type === "error" ? "bg-red-50 border-red-300 text-red-800" :
            "bg-linen border-sand text-worker"
          }`} data-testid="verification-feedback">
            <AlertCircle size={16} className="mt-0.5" />
            <span className="flex-1">{feedback.message}</span>
            <button onClick={() => setFeedback(null)} className="text-current/70 hover:text-current">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Already verified */}
        {isVerified && (
          <div className="card-base p-8" data-testid="verified-state">
            <div className="flex items-start gap-4">
              <div className="bg-hive rounded-full p-3 shrink-0">
                <Check size={28} className="text-worker" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-worker">{t("verificationX.verifiedTitle")} 🛡️</h3>
                <p className="mt-2 text-smoke">
                  {t("verificationX.verifiedMethod")} <strong className="capitalize">{(status?.verification_method || "manual").replace("_", " ")}</strong>
                  {status?.verified_at && (
                    <> · {t("verificationX.verifiedAt", { date: formatDate(status.verified_at) })}</>
                  )}
                </p>
                <p className="mt-3 text-sm text-smoke">
                  {t("verificationX.verifiedFootnote")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pending review */}
        {!isVerified && pending && (
          <div className="card-base p-6 mb-6 border-amber-400" data-testid="pending-state">
            <div className="flex items-start gap-3">
              <Clock size={22} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-bold text-worker">
                  {pending.status === "in_review" ? t("verificationX.pendingInReview") : pending.status === "requires_input" ? t("verificationX.pendingActionNeeded") : t("verificationX.pendingSubmitted")}
                </h3>
                <p className="mt-1 text-sm text-smoke">
                  {pending.method === "stripe_identity" ? t("verificationX.pendingStripeBody") : t("verificationX.pendingManualBody")}
                </p>
                {pending.rejection_reason && (
                  <p className="mt-2 text-xs text-amber-700 font-mono">{pending.rejection_reason}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Two paths */}
        {!isVerified && !pending && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="verification-options">
            {/* Stripe Identity */}
            <div className="card-base p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-hive/30 blur-2xl" />
              <div className="relative">
                <ShieldCheck size={28} className="text-hive-ink" />
                <h3 className="mt-3 font-display text-2xl font-bold text-worker">{t("verificationX.stripeTitle")}</h3>
                <p className="mt-2 text-sm text-smoke">
                  {t("verificationX.stripeBody")}
                </p>
                <ul className="mt-4 space-y-2 text-sm text-worker">
                  <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.stripeFeat1")}</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.stripeFeat2")}</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.stripeFeat3")}</li>
                </ul>
                <button onClick={startStripeIdentity} disabled={busy} className="fx-btn fx-btn--primary mt-6 w-full" data-testid="start-stripe-identity-btn">
                  {busy ? <><Loader2 className="animate-spin" size={16} /> {t("verificationX.submitting")}</> : <>{t("verificationX.stripeBtn")} <ArrowUpRight size={16} /></>}
                </button>
              </div>
            </div>

            <div className="card-base p-6">
              <FileText size={28} className="text-smoke" />
              <h3 className="mt-3 font-display text-2xl font-bold text-worker">{t("verificationX.manualTitle")}</h3>
              <p className="mt-2 text-sm text-smoke">
                {t("verificationX.manualBody")}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-worker">
                <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.manualFeat1")}</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.manualFeat2")}</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-hive-ink" /> {t("verificationX.manualFeat3")}</li>
              </ul>
              <button onClick={() => setShowManual(true)} className="fx-btn fx-btn--secondary mt-6 w-full" data-testid="start-manual-verification-btn">
                {t("verificationX.manualBtn")}
              </button>
            </div>
          </div>
        )}
      </div>

      {showManual && (
        <ManualVerificationModal
          onClose={() => setShowManual(false)}
          onSubmitted={() => { setShowManual(false); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function ManualVerificationModal({ onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_legal_name: "",
    document_type: "drivers_license",
    document_url: "",
    selfie_url: "",
    business_license_url: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/verification/manual", {
        ...form,
        selfie_url: form.selfie_url || null,
        business_license_url: form.business_license_url || null,
        notes: form.notes || null,
      });
      onSubmitted();
    } catch (err) {
      setError(errorMessage(err, t("verificationX.couldNotSubmit")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="manual-verification-modal">
      <form onSubmit={submit} className="bg-cream rounded-md max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-smoke hover:text-worker" data-testid="close-manual-modal">
          <X size={20} />
        </button>
        <h3 className="font-display text-2xl font-bold text-worker mb-1">{t("verificationX.modalTitle")}</h3>
        <p className="text-sm text-smoke mb-5">
          {t("verificationX.modalSubtitle")}
        </p>

        <div className="space-y-4">
          <div>
            <label className="label-base">{t("verificationX.legalName")}</label>
            <input
              type="text"
              value={form.full_legal_name}
              onChange={update("full_legal_name")}
              className="input-base"
              required
              data-testid="legal-name-input"
            />
          </div>

          <div>
            <label className="label-base">{t("verificationX.docType")}</label>
            <select
              value={form.document_type}
              onChange={update("document_type")}
              className="input-base"
              data-testid="doc-type-select"
            >
              {DOC_TYPES.map((d) => (
                <option key={d} value={d}>{t(`verificationX.docTypes.${d}`)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-base">{t("verificationX.docUrl")}</label>
            <input
              type="url"
              value={form.document_url}
              onChange={update("document_url")}
              placeholder={t("verificationX.docUrlPlaceholder")}
              className="input-base"
              required
              data-testid="doc-url-input"
            />
          </div>

          <div>
            <label className="label-base">{t("verificationX.selfieUrl")} <span className="text-smoke normal-case">{t("verificationX.recommended")}</span></label>
            <input
              type="url"
              value={form.selfie_url}
              onChange={update("selfie_url")}
              placeholder={t("verificationX.selfiePh")}
              className="input-base"
              data-testid="selfie-url-input"
            />
          </div>

          <div>
            <label className="label-base">{t("verificationX.businessUrl")} <span className="text-smoke normal-case">{t("verificationX.ifYouHave")}</span></label>
            <input
              type="url"
              value={form.business_license_url}
              onChange={update("business_license_url")}
              placeholder={t("verificationX.businessPh")}
              className="input-base"
              data-testid="business-url-input"
            />
          </div>

          <div>
            <label className="label-base">{t("verificationX.notes")} <span className="text-smoke normal-case">({t("common.optional")})</span></label>
            <textarea
              value={form.notes}
              onChange={update("notes")}
              rows={2}
              className="input-base resize-none"
              maxLength={400}
              data-testid="notes-input"
            />
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-300 rounded-sm p-2 flex items-start gap-1" data-testid="manual-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{typeof error === "string" ? error : t("verificationX.couldNotSubmit")}</span>
            </div>
          )}

          <button type="submit" disabled={busy} className="fx-btn fx-btn--primary w-full" data-testid="submit-manual-btn">
            {busy ? <><Loader2 className="animate-spin" size={16} /> {t("verificationX.submitting")}</> : <>{t("verificationX.submitReview")}</>}
          </button>
        </div>
      </form>
    </div>
  );
}
